import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Participant, Team } from '../../_shared/models/team.model';
import { AuthService } from '../../auth/auth.service';
import { User } from '../../_shared/models/user.model';
import { BehaviorSubject, lastValueFrom, Observable, Subject } from 'rxjs';
import { th } from 'date-fns/locale';

@Injectable({ providedIn: 'root' })
export class TeamDetailsService {
  teamInfo: Team;
  teamUpdate$ = new Subject<Team>();
  teamDelete$ = new Subject<string>();
  addOwner$ = new Subject<User[]>();
  updateStaff$ = new Subject<boolean>();
  organisationId$ = new BehaviorSubject<string | null>(null);
  private updateParticipants$ = new Subject<boolean>();
  private participants$ = new Subject<Participant[]>();
  private updateFans$ = new Subject<boolean>();

  constructor(private http: HttpClient, private authService: AuthService) { }

  // Get team info
  async info(teamId: string): Promise<Team> {
    const response = await lastValueFrom(
      this.http.get<any>('api_endpoint' + `/team/${teamId}/info`)
    );
    this.teamInfo = response.data;
    return this.teamInfo;
  }

  getParticiapnts(teamId: string, seasonId, role: string) {
    return this.http.post<any>(
      'api_endpoint' + `/team/${teamId}/participants`,
      { seasonId, role }
    );
  }

  getOwners(teamId: string) {
    return this.http.get<any>('api_endpoint' + `/team/${teamId}/owners?includeCoaches=true`);
  }

  removeOwner(teamId: string, ownerId: string) {
    return this.http.post<any>(
      'api_endpoint' + `/team/${teamId}/remove-owner`,
      { ownerId }
    );
  }

  synchronizeTeamRoster(teamId: string, seasonId, partner: string) {
    return this.http.post<any>(
      'api_endpoint' + `/team/${teamId}/syncTeamRoster`,
      { partner, seasonId }
    );
  }

  authenticateOwner(teamId: string) {
    return this.http.post<any>(
      'api_endpoint' + `/team/${teamId}/authenticate`,
      {}
    );
  }

  getTeamCoaches(teamId: string): Observable<any> {
    return this.http.post<any>('api_endpoint' + `/team/${teamId}/participants`, {
      role: 'coach',
    }, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  getFollowers(teamId: string): Observable<any> {
    return this.http.get<any>('api_endpoint' + `/team/${teamId}/followers`);
  }

  follow(teamId: string, tieId?: string) {
    return this.http.post<any>('api_endpoint' + `/team/follow`, { teamId, tieId });
  }

  unfollow(teamId: string, tieId?: string) {
    return this.http.post<any>('api_endpoint' + `/team/unfollow`, { teamId, tieId });
  }

  verifyTeam(teamId: string) {
    return this.http.post<any>('api_endpoint' + `/admin/verify-team`, {
      teamId,
    });
  }

  startClaimCoach(teamId: string, email: string) {
    return this.http.post<any>('api_endpoint' + `/user/${teamId}/claim/coach`, { email: email });
  }

  reInitiateClaimCoach(teamId: string, email: string) {
    return this.http.post<any>(
      'api_endpoint' + `/user/${teamId}/claim/coach/re-initiate`,
      { email: email }
    );
  }

  verifyClaimCoach(teamId: string, code: string) {
    return this.http.post<any>(
      'api_endpoint' + `/user/${teamId}/claim/coach/validate`,
      { code: code }
    );
  }

  isOwner(teamOwners: string[]): boolean {
    return teamOwners?.some(
      (owner) => owner === this.authService.authUserSignal()?._id
    );
  }

  joinTeam(teamId: string): Observable<any> {
    return this.http.post<any>('api_endpoint' + `/team/${teamId}/join-team/player`, {})
  }

  claimUserAsStaff(teamId: string, userId: string, roleName: string): Observable<any> {
    return this.http.post<any>('api_endpoint' + `/user/${userId}/${teamId}/claim-request/${roleName}`, {});
  }

  approveOrRejectStaffRequest(userId: string, teamId: string, seasonId: string, roleName: string, status: string) {
    return this.http.put<any>('api_endpoint' + `/user/${userId}/${teamId}/claim-status-update/${roleName}/${status}`, { seasonId: seasonId });
  }

  async isOfficial(officials: any[]): Promise<boolean> {
    const authUser: User = await this.authService.getAuthUser();
    return officials?.some((official) => official.user === authUser._id);
  }

  publishTeamUpdate(team: Team) {
    // type can be individual or tie
    this.teamUpdate$.next(team);
  }

  subscribeTeamUpdate(): Observable<Team> {
    return this.teamUpdate$.asObservable();
  }

  publishDeleteTeam(teamId: string) {
    this.teamDelete$.next(teamId);
  }

  subscribeDeleteTeam(): Observable<string> {
    return this.teamDelete$.asObservable();
  }

  publishAddOwner(owners: User[]) {
    this.addOwner$.next(owners);
  }

  subscribeAddOwner(): Observable<User[]> {
    return this.addOwner$.asObservable();
  }

  publishUpdateStaff(update: boolean) {
    this.updateStaff$.next(update);
  }

  subscribeUpdateStaff(): Observable<boolean> {
    return this.updateStaff$.asObservable();
  }


  publishUpdateParticipants(update: boolean) {
    this.updateParticipants$.next(update);
  }
  
  subscribeUpdateParticipants(): Observable<boolean> {
    return this.updateParticipants$.asObservable();
  }


  subscribePublishParticipants(): Observable<Participant[]> {
    return this.participants$.asObservable();
  }

  publishParticipants(participant: Participant[]) {
    this.participants$.next(participant);
  }

  publishOrganisationId(id: string | null) {
    this.organisationId$.next(id);
  }

  subscribeOrganisationId() {
    return this.organisationId$.asObservable();
  }

  publishUpdateFans(update: boolean) {
    this.updateFans$.next(update);
  }

  subscribeUpdateFans(): Observable<boolean> {
    return this.updateFans$.asObservable();
  }


}
